// TODO: Support Optional Fields ( Fields are required by default )

import {
  useField as useFieldApi,
  useForm as useTanStackForm,
} from "@tanstack/react-form";
import type {
  AnyFieldApi,
  DeepKeys,
  DeepValue,
  FieldApi,
  FormOptions,
  ReactFormExtendedApi,
  StandardSchemaV1,
} from "@tanstack/react-form";
import type { UseFieldOptions } from "node_modules/@tanstack/react-form/dist/esm/types";
import {
  type ChangeEvent,
  type ComponentProps,
  type FC,
  type ReactNode,
  useMemo,
} from "react";
import type { Except } from "type-fest";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import type { FormInputEvent } from "@/types/utils";
import { createContextFactory } from "@/utils/common";
import { cn } from "@/utils/ui";
import { LoaderCircleIcon } from "lucide-react";
import { Slot } from "radix-ui";

interface FieldLabelProps extends ComponentProps<typeof Label> {}
interface FieldDetailProps extends ComponentProps<"p"> {
  asChild?: boolean;
}
interface FieldMessageProps extends ComponentProps<"p"> {
  asChild?: boolean;
}
interface FieldContainerProps extends ComponentProps<"div"> {
  label?: string;
  detail?: string;
  message?: string;
  disableController?: boolean;
  showLabel?: boolean;
}
interface FieldControllerProps extends Slot.SlotProps {}

interface FieldApiExtended<
  TParentData,
  TName extends DeepKeys<TParentData>,
  TData extends DeepValue<TParentData, TName> = DeepValue<TParentData, TName>,
> extends FieldApi<
    TParentData,
    TName,
    TData,
    StandardSchemaV1<TData, unknown>,
    StandardSchemaV1<TData, unknown>,
    StandardSchemaV1<TData, unknown>,
    StandardSchemaV1<TData, unknown>,
    StandardSchemaV1<TData, unknown>,
    StandardSchemaV1<TData, unknown>,
    StandardSchemaV1<TData, unknown>,
    StandardSchemaV1<TParentData, unknown>,
    StandardSchemaV1<TParentData, unknown>,
    StandardSchemaV1<TParentData, unknown>,
    StandardSchemaV1<TParentData, unknown>,
    StandardSchemaV1<TParentData, unknown>,
    StandardSchemaV1<TParentData, unknown>,
    StandardSchemaV1<TParentData, unknown>,
    StandardSchemaV1<TParentData, unknown>,
    StandardSchemaV1<TParentData, unknown>
  > {
  Label: FC<FieldLabelProps>;
  Detail: FC<FieldDetailProps>;
  Message: FC<FieldMessageProps>;
  Container: FC<FieldContainerProps>;
  Controller: FC<FieldControllerProps>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  handleChangeExtended: (value: any) => void;
}

interface FieldComponentProps<
  TParentData,
  TName extends DeepKeys<TParentData>,
  TData extends DeepValue<TParentData, TName> = DeepValue<TParentData, TName>,
> extends UseFieldOptions<
    TParentData,
    TName,
    TData,
    StandardSchemaV1<TData, unknown>,
    StandardSchemaV1<TData, unknown>,
    StandardSchemaV1<TData, unknown>,
    StandardSchemaV1<TData, unknown>,
    StandardSchemaV1<TData, unknown>,
    StandardSchemaV1<TData, unknown>,
    StandardSchemaV1<TData, unknown>,
    StandardSchemaV1<TParentData, unknown>,
    StandardSchemaV1<TParentData, unknown>,
    StandardSchemaV1<TParentData, unknown>,
    StandardSchemaV1<TParentData, unknown>,
    StandardSchemaV1<TParentData, unknown>,
    StandardSchemaV1<TParentData, unknown>,
    StandardSchemaV1<TParentData, unknown>,
    StandardSchemaV1<TParentData, unknown>,
    StandardSchemaV1<TParentData, unknown>
  > {
  render: (fieldApi: FieldApiExtended<TParentData, TName, TData>) => ReactNode;
}

type FieldComponent<TParentData> = <
  TName extends DeepKeys<TParentData>,
  TData extends DeepValue<TParentData, TName> = DeepValue<TParentData, TName>,
>({
  render,
  ...fieldOptions
}: Except<FieldComponentProps<TParentData, TName, TData>, "form">) => ReactNode;

type FormExtended<TFormData> = ReactFormExtendedApi<
  TFormData,
  StandardSchemaV1<TFormData, unknown>,
  StandardSchemaV1<TFormData, unknown>,
  StandardSchemaV1<TFormData, unknown>,
  StandardSchemaV1<TFormData, unknown>,
  StandardSchemaV1<TFormData, unknown>,
  StandardSchemaV1<TFormData, unknown>,
  StandardSchemaV1<TFormData, unknown>,
  StandardSchemaV1<TFormData, unknown>,
  StandardSchemaV1<TFormData, unknown>
> & {
  Root: FC<ComponentProps<"form">>;
  Field: FieldComponent<TFormData>;
  Submit: FC<ComponentProps<typeof Button>>;
};

const [FieldContextProvider, useFieldContext] =
  createContextFactory<AnyFieldApi>();

function useForm<TFormData>(
  schema: StandardSchemaV1<TFormData, unknown>,
  options?: FormOptions<
    TFormData,
    StandardSchemaV1<TFormData, unknown>,
    StandardSchemaV1<TFormData, unknown>,
    StandardSchemaV1<TFormData, unknown>,
    StandardSchemaV1<TFormData, unknown>,
    StandardSchemaV1<TFormData, unknown>,
    StandardSchemaV1<TFormData, unknown>,
    StandardSchemaV1<TFormData, unknown>,
    StandardSchemaV1<TFormData, unknown>,
    StandardSchemaV1<TFormData, unknown>
  >,
): FormExtended<TFormData> {
  const form = useTanStackForm({
    validators: {
      onChange: schema,
    },
    ...options,
  });

  const FormRoot = ({ className, ...props }: ComponentProps<"form">) => (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
      className={className}
      {...props}
    />
  );

  const FormField: FieldComponent<TFormData> = ({ render, ...props }) => (
    <form.Field {...props}>
      {(field) => (
        <FieldContextProvider value={field}>
          {render(
            Object.assign(field, {
              Label: FieldLabel,
              Detail: FieldDetail,
              Message: FieldMessage,
              Container: FieldContainer,
              Controller: FieldController,
              handleChangeExtended: handleChangeExtended(field),
            }),
          )}
        </FieldContextProvider>
      )}
    </form.Field>
  );

  const FormSubmit = ({
    className,
    children,
    ...props
  }: ComponentProps<typeof Button>) => (
    <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
      {([canSubmit, isSubmitting]) => (
        <Button
          type="submit"
          data-submitting={isSubmitting}
          data-can-submit={canSubmit}
          disabled={isSubmitting || !canSubmit}
          className={className}
          {...props}
        >
          {isSubmitting && <LoaderCircleIcon className="animate-spin" />}
          {children}
        </Button>
      )}
    </form.Subscribe>
  );

  return {
    ...form,
    Root: FormRoot,
    Field: FormField,
    Submit: FormSubmit,
  } as FormExtended<TFormData>;
}

function useField() {
  const fieldContext = useFieldContext();
  const fieldApi = useFieldApi({
    form: fieldContext.form,
    name: fieldContext.name,
  });

  return Object.assign(fieldContext, {
    // for some reason, when using `fieldContext.state` here, the state is not updated.
    ...fieldApi.state.meta,
    hasErrors: fieldApi.state.meta.errors.length > 0,
  });
}

function FieldLabel({ className, children, ...props }: FieldLabelProps) {
  const field = useField();

  if (children === undefined) {
    return null;
  }

  return (
    <Label
      htmlFor={field.name.toString()}
      className={cn(
        field.isTouched && field.hasErrors && "text-destructive-fg-primary",
        className,
      )}
      {...props}
    >
      {children}
    </Label>
  );
}

function FieldDetail({
  asChild,
  className,
  children,
  ...props
}: FieldDetailProps) {
  const Comp = asChild ? Slot.Root : "p";

  if (children === undefined) {
    return null;
  }

  return (
    <Comp className="text-sm text-gray-fg-secondary" {...props}>
      {children}
    </Comp>
  );
}

function resolveFieldMessage(field: ReturnType<typeof useField>): ReactNode {
  const errorEntry = field.state.meta.errors[0] as unknown;
  if (!errorEntry) {
    return null;
  }
  if (typeof errorEntry === "string") {
    return errorEntry;
  }
  if (typeof errorEntry === "object" && "message" in errorEntry) {
    return errorEntry.message as string;
  }

  return null;
}

function FieldMessage({
  asChild,
  className,
  children,
  ...props
}: FieldMessageProps) {
  const field = useField();

  const Comp = asChild ? Slot.Root : "p";
  const hasPlaceholder = children !== undefined;
  // biome-ignore lint/correctness/useExhaustiveDependencies: We only want to update the message when the errors change
  const message = useMemo(
    () =>
      field.isDirty && field.hasErrors ? resolveFieldMessage(field) : null,
    [field.hasErrors],
  );

  if (!hasPlaceholder && !message) {
    return null;
  }

  return (
    <Comp
      className={cn(
        "text-sm",
        field.isTouched && field.hasErrors
          ? "font-medium text-destructive-fg-primary"
          : "text-gray-fg-secondary",
        className,
      )}
      {...props}
    >
      {message || children}
    </Comp>
  );
}

function FieldContainer({
  label,
  detail,
  message,
  disableController,
  showLabel,
  className,
  children,
  ...props
}: FieldContainerProps) {
  return (
    <div className={cn("grid gap-1", className)} {...props}>
      {label && (
        <FieldLabel className={cn(!showLabel && "sr-only")}>{label}</FieldLabel>
      )}
      {disableController ? (
        children
      ) : (
        <FieldController>{children}</FieldController>
      )}
      <FieldDetail>{detail}</FieldDetail>
      <FieldMessage>{message}</FieldMessage>
    </div>
  );
}

function FieldController({ children, ...props }: FieldControllerProps) {
  const field = useField();

  return (
    <Slot.Root
      {...{
        id: field.name.toString(),
        name: field.name.toString(),
        value: field.state.value ?? "",
        onChange: handleChangeExtended(field),
        onBlur: field.handleBlur,
        "aria-invalid": field.hasErrors,
      }}
      {...props}
    >
      {children}
    </Slot.Root>
  );
}

function handleChangeExtended(field: AnyFieldApi) {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  return (value: any) => {
    // TODO: this can be better
    const isOptional =
      field.form.options.defaultValues[field.name] === undefined;

    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    let valueFromInput: any = undefined;
    if (isInputChangeEvent(value)) {
      const inputMode = value.target
        .inputMode as ComponentProps<"input">["inputMode"];
      const inputType = value.target.type as ComponentProps<"input">["type"];
      const inputValue = (() => {
        switch (inputType) {
          case "number":
          case "range":
            return value.target.valueAsNumber;

          case "date":
          case "time":
            return value.target.valueAsDate;

          case "checkbox":
          case "radio":
            return value.target.checked;

          case "file":
            return value.target.files;

          default: {
            if (inputMode === "numeric" || inputMode === "decimal") {
              return value.target.valueAsNumber;
            }

            return value.target.value;
          }
        }
      })();

      valueFromInput = inputValue;
    } else if (isTextareaChangeEvent(value)) {
      valueFromInput = value.target.value;
    } else if (isFormInputEvent(value)) {
      valueFromInput = value.value;
    } else {
      console.warn("handleChangeExtended: unsupported event type", {
        value,
        field,
      });
    }

    const finalValue = valueFromInput ?? value;
    const isEmpty = finalValue?.toString().length === 0;

    field.handleChange(isOptional && isEmpty ? undefined : finalValue);
  };
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
function isChangeEvent(value: any): value is ChangeEvent<any> {
  return value?.target !== undefined && value.target?.value !== undefined;
}

function isInputChangeEvent(
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  value: any,
): value is ChangeEvent<HTMLInputElement> {
  if (!isChangeEvent(value)) {
    return false;
  }

  const target = value.target;

  return (
    target instanceof HTMLInputElement &&
    target.tagName === "INPUT" &&
    typeof target.type === "string" &&
    typeof target.value === "string"
  );
}

function isTextareaChangeEvent(
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  value: any,
): value is ChangeEvent<HTMLTextAreaElement> {
  if (!isChangeEvent(value)) {
    return false;
  }

  const target = value.target;

  return (
    target instanceof HTMLTextAreaElement &&
    target.tagName === "TEXTAREA" &&
    typeof target.value === "string"
  );
}

function isFormInputEvent(
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  value: any,
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
): value is FormInputEvent<any> {
  return value?.value !== undefined;
}

export { useForm };
